import React, { useState } from 'react';
import { styled } from 'linaria/react'
import { css } from 'linaria'
import Tooltip from 'react-tooltip-lite'
import { ReactComponent as PrebookIcon } from '../../../svg/Prebook.svg'
import { ReactComponent as OutOfStockIcon } from '../../../svg/OutOfStock.svg'
import { ReactComponent as InStockIcon } from '../../../svg/InStock.svg'
import { getPackageStockStatus, isArrayWithItems } from '../../Core/Util/Helpers';
import { useIntl } from '@jetshop/intl'
import { STOCK_ID } from '../../Core/Util/Constants';

const StockContainer = styled.div`
    .product-stock {
        font-size: var(--fontsize-075);
        font-weight: 600;
    }

    .product-stock svg {
        width: 24px;
        height: 24px;
    }
`

const warehouseTooltipStyles = css`
   .react-tooltip-lite {
      color: #000000;
      background-color: #e6eae3;
      font-size: 14px;
      border-radius: 4px;
      font-weight: 500;
      font-family: 'Industry', sans-serif;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
   }
   .react-tooltip-lite-arrow {
      margin-top: 2px !important;
      color: #e6eae3 !important;
      width: 0px !important;
   }
`

const WarehouseTooltipDiv = styled.div`
   font-family: 'Industry', sans-serif;
   width: fit-content !important;
   .title {
      font-weight: 600;
   }
`

const WarehouseInfoContainer = styled.div`
    .warehouse-toggle {
        cursor: pointer;
        position: relative;
        display: inline-block;
    }
`

const WAREHOUSE_IN_STOCK_ID = 1
const WAREHOUSE_LOW_STOCK_ID = 2
const WAREHOUSE_OUT_OF_STOCK_ID = 3

const getWarehouseStockStatusId = (warehouseStockLevel) => {
   if (warehouseStockLevel >= 3 ) {
      return WAREHOUSE_IN_STOCK_ID
   } else if (warehouseStockLevel < 3 && warehouseStockLevel != 0 ) {
      return WAREHOUSE_LOW_STOCK_ID
   } else {
      return WAREHOUSE_OUT_OF_STOCK_ID
   }
}

const WarehouseStockInfo = ({ warehouseStockLevel, storeShelf }) => {
   const [showTooltip, setShowTooltip] = useState(false)

   // Generate a class name for SEO purposes based on the warehouseStockLevel.
   const seoStockStatusClassName = `boras-${getWarehouseStockStatusId(warehouseStockLevel)}`

   // Get shelf information
   const shelfInfo = storeShelf || 'Hyllplats saknas'

   const formatShelfInfo = (shelfInfo) => {
      let formattedShelfInfo = shelfInfo
      if(formattedShelfInfo.includes('E')){
         formattedShelfInfo = formattedShelfInfo.replace('E', 'Entréplan')
      }
      if(formattedShelfInfo.includes('V')){
         formattedShelfInfo = formattedShelfInfo.replace('V', 'Vägg')
      }
      if(formattedShelfInfo.includes('Ö')){
         formattedShelfInfo = formattedShelfInfo.replace('Ö', 'Övervåning')
      }
      if(formattedShelfInfo.includes('K') && formattedShelfInfo.includes('Entréplan')){
         formattedShelfInfo = formattedShelfInfo.replace('K', 'Kassa')
      }
      if(formattedShelfInfo.includes('G') && formattedShelfInfo.includes('Entréplan')){
         formattedShelfInfo = formattedShelfInfo.replace('G', 'Golvyta')
      }
      if(formattedShelfInfo.includes('K') && formattedShelfInfo.includes('Övervåning')){
         formattedShelfInfo = formattedShelfInfo.replace('K', 'Kemrum')
      }
      return formattedShelfInfo
   }

   const handleToggleTooltip = () => {
      setShowTooltip(!showTooltip)
   }

   return (
      <WarehouseInfoContainer>
         <Tooltip
            content={
               <WarehouseTooltipDiv>
                  <div className="title">Hyllplats i butiken</div>
                  <div>{formatShelfInfo(shelfInfo)}</div>
               </WarehouseTooltipDiv>
            }
            direction="up"
            arrowSize={6}
            distance={10}
            useDefaultStyles={false}
            tipContentClassName={warehouseTooltipStyles}
            useHover={false}
            isOpen={showTooltip}
         >
            <span className={`${seoStockStatusClassName} product-stock d-flex align-items-center`}>
               {warehouseStockLevel >= 3 ? (
                  <>
                     <InStockIcon className="pr-8"></InStockIcon>
                     <span className="warehouse-toggle link link--vb" onClick={handleToggleTooltip}>
                        {'Boråsbutiken'}
                     </span>
                  </>
               ) : warehouseStockLevel < 3 && warehouseStockLevel != 0 ? (
                  <>
                     <PrebookIcon className="pr-8"></PrebookIcon>
                     <span className="warehouse-toggle link link--vb" onClick={handleToggleTooltip}>
                        {'Boråsbutiken'}
                     </span>
                  </>
               ) : (
                  <>
                     <OutOfStockIcon className="pr-8"></OutOfStockIcon>
                     <span className="warehouse-toggle link link--vb" onClick={handleToggleTooltip}>
                        {'Boråsbutiken'}
                     </span>
                  </>
               )}
            </span>
         </Tooltip>
      </WarehouseInfoContainer>
   )
}

const ProductStockStatus = ({activeVariant, includeWarehouse, packageProducts, productInformation}) => {
   const t = useIntl()
   const {stockStatus, warehouseStock} = activeVariant
   const { storeShelf } = productInformation || {}

   const isMainVariantPackage = activeVariant?.isPackage === true
   const isPackageItems = isMainVariantPackage && isArrayWithItems(packageProducts)

   const packageStatusInfo =
      isPackageItems && activeVariant?.stockStatus?.buyable === true
         ? getPackageStockStatus(packageProducts)
         : {stockStatusId: STOCK_ID.OUT_OF_STOCK, text: t('stock status out of stock') } // Out of stock
         
    const stockStatusId = isMainVariantPackage ? packageStatusInfo?.stockStatusId : stockStatus?.stockStatusId

    // If isMainVariantPackage, use the constructed packageStatusInfoText
    const text = isMainVariantPackage ? packageStatusInfo?.text : stockStatus?.text;

    const warehouseStockLevel = warehouseStock?.length > 0 ? warehouseStock[0]?.stockLevel : 0;

    // Generate a class name for SEO purposes based on the stock status ID.
    const seoStockStatusClassName = `online-${stockStatusId}`

    return (
       <StockContainer className="my-8">
          <span className="product-stock d-flex align-items-center gap-3">
             <div>
                <span className={`${seoStockStatusClassName} product-stock d-flex align-items-center`}>
                   {(() => {
                      if(STOCK_ID.IN_STOCK.includes(stockStatusId)){
                        return (
                           <>
                              <InStockIcon className="pr-8"></InStockIcon>
                              {text}{' '}
                           </>
                        )
                      }else if(STOCK_ID.PREBOOK.includes(stockStatusId)){
                        return (
                           <>
                              <PrebookIcon className="pr-8"></PrebookIcon>
                              {text}{' '}
                           </>
                        )

                      }else{
                        return (
                           <>
                              <OutOfStockIcon className="pr-8"></OutOfStockIcon>
                              {text}{' '}
                           </>
                        )
                      }
                   })()}
                </span>
             </div>
             <div>
                {includeWarehouse && !isMainVariantPackage && <WarehouseStockInfo warehouseStockLevel={warehouseStockLevel} storeShelf={storeShelf}></WarehouseStockInfo>}
             </div>
          </span>
       </StockContainer>
    )
}
 
export default ProductStockStatus;